import React, { useEffect, useState } from 'react'

import { Link, useParams, useHistory } from 'react-router-dom'

import { Tabs, PageHeader, Button, Divider, Alert, Popconfirm, Space } from 'antd'

import {
  DollarOutlined,
  OrderedListOutlined,
  FolderOpenOutlined,
  DashboardOutlined,
  CheckSquareOutlined,
  EditOutlined,
  ClockCircleOutlined,
  CommentOutlined,
} from '@ant-design/icons'

import { LAYOUT_MANAGER_JOB_EDIT } from '../../navigation'
import {
  useFetch,
  useBitacoraMessage,
  useUpdatePrices,
  useClientConfirmation,
  useCreateStorage
} from '../../../../hooks/manager/job'
import { useStorage, isStorageAvailable } from '../../../../hooks/common/storage'
import { useCacheVendor, getVendorsForSelect } from '../../../../hooks/cache/vendor'

import { INVOICE_STATUS_IN_REVIEW, PURCHASE_ORDER_STATUS_IN_REVIEW } from '../../../../config/const'

import Lqa from './lqa'
import FormUpdateState from '../../../../components/job/status/modalV2'

import TasksList from './tasks'
import ClientFinances from './clientFinances'
import VendorFinances from './vendorFinances'
import Details from '../../../../components/job/show/details'
import { flashSuccess, flashError } from '../../../../core/utils/flashMessage'
import Bitacora from '../../../../components/bitacora/v2'
import Loader from '../../../../components/loader'

import FileManager from './files'

import ServiceTab from './serviceTab'
import SatisfactionBanner from './banner/satisfaction'

const { TabPane } = Tabs

const Show = () => {
  const { id } = useParams()
  const history = useHistory()
  const [modalStatusVisible, setModalStatusVisible] = useState(false)
  const onShowModal = () => setModalStatusVisible(true)
  const onHideModal = () => setModalStatusVisible(false)

  const onPressBack = () => history.goBack()

  const { data, isLoading, isError } = useFetch(id)
  const { data: storageData, isLoading: isStorageLoading, isError: isStorageError } = useStorage()

  const { data: vendorsRaw, isLoading: isVendorLoading } = useCacheVendor()
  const vendors = getVendorsForSelect(vendorsRaw)

  const { mutate: postBitacoraMessage, isLoading: isBitacoraLoading } = useBitacoraMessage(id)
  const { mutate: updatePrices, isLoading: isUpdatePriceLoading } = useUpdatePrices(id)
  const { mutate: clientConfirmation, isLoading: isClientConfirmationLoading } = useClientConfirmation(id)
  const { mutate: createStorage, isLoading: isCreateStorageLoading } = useCreateStorage(id)

  const actionLoading = isBitacoraLoading || isUpdatePriceLoading || isClientConfirmationLoading || isCreateStorageLoading

  useEffect(() => {
    window.scrollTo(0, 0)
  }, [])

  if (isLoading || isStorageLoading || isVendorLoading) {
    return (<Loader loading />)
  }
  if (isError || isStorageError) {
    return null
  }

  const storageAvailable = isStorageAvailable(storageData)

  const _hasRelationsWithInvoicesOrPO = () => {
    const { invoices, purchaseOrders } = data
    return invoices.length > 0 || purchaseOrders.length > 0
  }

  const _allowEditStrong = () => {
    // SI ESTA INVOLUCRADO EN UNA INVOICE O PO NO SE PUEDE
    // CARGAR TAREA, CAMBIAR ESTADO
    const { invoices, purchaseOrders } = data
    if (data && data.job && data.job.status === 'Rejected') {
      return false
    }
    return invoices.length === 0 && purchaseOrders.length === 0
  }

  const _allowEdit = () => {
    const { invoices, purchaseOrders, receivables, job } = data

    if (data && data.job && data.job.status === 'Rejected') {
      return false
    }

    // CONDICIÓN FUERTE
    // if (data && data.job && (data.job.status == JOB_STATUS_COMPLETED || data.job.status == JOB_STATUS_DELIVERED)) {
    //    return false;
    // }

    // NO SE PUEDE EDITAR SI
    // EL JOB ESTA INCLUIDO EN UNA INVOICE CON ESTADO != IN REVIEW
    const invoicesInReview = invoices.reduce(function (r, i) {
      return r && i.status === INVOICE_STATUS_IN_REVIEW
    }, true)
    // EL JOB ESTÁ EN MAS DE UNA INVOICE CON ESTADO == IN REVIEW
    const justOneInvoiceInReview = invoices.filter((i) => i.status === INVOICE_STATUS_IN_REVIEW).length <= 1
    // EL JOB TIENE UNA RECTIFICACION
    const jobNotHasRectification = receivables.reduce((r, i) => r && !i.rectification, true)
    // ALGUNA TASK ESTA INCLUIDA EN UNA PO CON ESTADO != IN REVIEW
    const purchseOrderInReview = purchaseOrders.reduce(function (r, i) {
      return r && i.status === PURCHASE_ORDER_STATUS_IN_REVIEW
    }, true)
    // ALGUNA TASK ESTÁ EN MAS DE UNA PO CON ESTADO == IN REVIEW
    const purchaseOrdersByVendor = {}
    purchaseOrders.forEach(function (a) {
      purchaseOrdersByVendor[a.vendorId] = purchaseOrdersByVendor[a.vendorId] || []
      purchaseOrdersByVendor[a.vendorId].push(a)
    })
    let justOnePurchaseOrderInReview = true
    Object.keys(purchaseOrdersByVendor).forEach((e) => {
      justOnePurchaseOrderInReview =
            justOnePurchaseOrderInReview &&
            purchaseOrdersByVendor[e].filter((i) => i.status === PURCHASE_ORDER_STATUS_IN_REVIEW).length <= 1
    })

    // ALGUNA TASK TIENE UNA RECTIFICACION
    let tasksNotHasRectification = true
    job.tasks.forEach((t) => {
      t.payables.forEach((p) => {
        tasksNotHasRectification = tasksNotHasRectification && !p.rectification
      })
    })

    return (
      invoicesInReview &&
      justOneInvoiceInReview &&
      jobNotHasRectification &&
      purchseOrderInReview &&
      justOnePurchaseOrderInReview &&
      tasksNotHasRectification
    )
  }

  const _allowChangeStatus = () => {
    return _allowEditStrong()
  }

  const _createStorage = (form) => {
    createStorage(form, {
      onSuccess: () => {
        flashSuccess({ description: 'Folder created' })
      },
      onError: () => {
        flashError({ description: 'Unexpected error!' })
      }
    })
  }

  const allowEdit = _allowEdit()
  const allowEditStrong = _allowEditStrong()
  const allowChangeStatus = _allowChangeStatus()
  const hasRelationsWithInvoicesOrPO = _hasRelationsWithInvoicesOrPO()
  const disabled = !allowEdit
  return (
    <>
      <div>
        <SatisfactionBanner job={data.jobDto} />
        {allowEdit && data.jobDto.client_status === 'Pending' && (
          <Alert
            type='warning'
            message='Client is waiting for a confirmation.'
            description={' '}
            banner
            showIcon
            action={
              <Popconfirm
                title='Are you sure?'
                onConfirm={clientConfirmation}
                okText='Yes'
                cancelText='No'
                disabled={actionLoading}
              >
                <Button
                  size='small'
                  icon={<CheckSquareOutlined />}
                  disabled={actionLoading}
                >
                  Confirmed
                </Button>
              </Popconfirm>
            }
          />
        )}
        {hasRelationsWithInvoicesOrPO &&
          (allowEdit
            ? (<Alert
                type='info'
                message='Job related with Invoice / Purchase Order in review'
                description='The data you may change here, it will be reflected in the Invoice / Purchase Order'
                banner
                showIcon
                />)
            : (<Alert
                type='info'
                message='Job related with Invoice / Purchase Order accepted'
                banner
                showIcon
                />)
          )}
        <PageHeader
          ghost={false}
          className='site-page-header'
          onBack={onPressBack}
          title={`${data.jobDto.locked ? ' ' : ''}${data.jobDto.display_id}`}
          subTitle='Job'
          extra={[
            <Button
              key='changeState'
              type='default'
              size='small'
              icon={<EditOutlined />}
              onClick={onShowModal}
              disabled={!allowChangeStatus}
            >
              {data.jobDto.status}
            </Button>,
            <Link key='3' to={LAYOUT_MANAGER_JOB_EDIT(id)}>
              <Button type='primary' size='small' disabled={disabled}>
                <i className='fa fa-pencil-square-o' />
                <Divider type='vertical' />
                Update
              </Button>
            </Link>
          ]}
        >
          {modalStatusVisible && (
            <FormUpdateState
              id={id}
              status={data.jobDto.status}
              onCancel={onHideModal}
            />
          )}

          <Details job={data.jobDto} column={2} allowEdit={allowEdit} />
        </PageHeader>

        <Divider />

        {(data.agreement || []).length === 0 && (
          <Alert type='error' message='Please establish an agreement for this client.' banner showIcon />
        )}

        <Tabs
          defaultActiveKey='task'
          type='card'
          style={{ marginBottom: 32, arginTop: 32, backgroundColor: '#fff' }}
        >
          <TabPane
            tab={
              <span>
                {' '}
                <OrderedListOutlined /> Tasks{' '}
              </span>
            }
            key='tasks'
            style={{ minHeight: 400 }}
          >
            <TasksList
              job={data.job}
              allowEdit={allowEdit}
              allowNewTask={allowEditStrong}
              allowUpdateStatus={allowEditStrong}
              catlogs={data.catlogs}
            />
          </TabPane>

          <TabPane
            tab={
              <span>
                {' '}
                <DollarOutlined /> Client Finances{' '}
              </span>
            }
            key='clientFinances'
            style={{ minHeight: 400 }}
          >
            <ClientFinances
              id={id}
              job={data.job}
              invoices={data.invoices}
              receivables={data.receivables}
              tasks={data.job.tasks}
              allowEdit={allowEdit}
              updatePrices={updatePrices}
              actionLoading={actionLoading}
            />
          </TabPane>
          <TabPane
            tab={
              <span>
                {' '}
                <DollarOutlined /> Vendor Finances{' '}
              </span>
            }
            key='vendorFinances'
            style={{ minHeight: 400 }}
          >
            <VendorFinances
              id={id}
              tasks={data.job.tasks}
              allowEdit={allowEdit}
              updatePrices={updatePrices}
              actionLoading={actionLoading}
            />
          </TabPane>
          <TabPane
            tab={
              <span>
                <DashboardOutlined /> LQA{' '}
              </span>
            }
            key='lqa'
            style={{ minHeight: 400 }}
          >
            <Lqa
              job={data.job}
              jobDto={data.jobDto}
              qas={data.lqas}
              vendors={vendors}
            />
          </TabPane>

          <TabPane
            tab={
              <span>
                {' '}
                <ClockCircleOutlined /> Service{' '}
              </span>
            }
            key='service'
            style={{ minHeight: 400 }}
          >
            <ServiceTab job={data.jobDto} />
          </TabPane>

          {storageAvailable && (
            <TabPane
              tab={
                <span>
                  {' '}
                  <FolderOpenOutlined /> Files{' '}
                </span>
              }
              key='files'
              style={{ minHeight: 400 }}
            >
              <FileManager
                id={id}
                storage={data.jobDto.storage_private}
                defaults={storageData.defaults}
                onCreateStorage={_createStorage}
                templates={data.templates || []}
              />
            </TabPane>
          )}
          <TabPane
            tab={
              <span>
                {' '}
                <CommentOutlined /> Logs{' '}
              </span>
            }
            key='timeline'
            style={{ minHeight: 400, marginLeft: 25, marginTop: 5 }}
          >
            <Bitacora
              bitacora={data.jobDto.bitacora || []}
              handleSubmit={postBitacoraMessage}
            />
          </TabPane>
        </Tabs>
      </div>
    </>
  )
}

export default Show
